import React, { useEffect } from 'react';
import { iconeDaRotina } from '../constants/iconesRotinas';

const COR = {
  azul:    '#003399',
  azulBg:  '#eff6ff',
  borda:   '#e8eaed',
  texto:   '#1a1a2e',
  branco:  '#fff',
  muted:   '#888',
};

// ─── Modal reutilizável ───────────────────────────────────────────────────────
// r112: pode renderizar em 2 modos
//   modo CLÁSSICO (default): header simples, botões no rodapé
//   modo RICO (com rotina/descrição/botoesNoTopo): visual "Cadastro de Acessos"
//                                                  com ícone, descrição e botões no topo
//
// Uso clássico (sem mudança):
//   <Modal aberto={...} titulo="Novo" onFechar={...} onSalvar={...}>...</Modal>
//
// Uso novo (Usuários):
//   <Modal
//     aberto={...} titulo="Novo Usuário"
//     rotina="usuarios"
//     descricao="Cadastro dos usuários com acesso ao sistema."
//     botoesNoTopo={true}
//     largura="95vw"
//     ...
//   >...</Modal>

function Modal({
  aberto, titulo, onFechar, onSalvar, salvando,
  somenteLeitura, onEditar,
  abas, abaAtiva, onAba,
  children,
  largura = '600px',
  // r112: novos opcionais
  rotina,
  descricao,
  botoesNoTopo = false,
  // r132: botões customizados renderizados ANTES de Cancelar/Salvar (no header).
  // Útil pra ações como "Gerenciar Valores", "Imprimir", etc.
  botoesExtras,
}) {
  // Fecha com ESC
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onFechar(); };
    if (aberto) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [aberto, onFechar]);

  // Trava o scroll do body enquanto o modal está aberto
  useEffect(() => {
    document.body.style.overflow = aberto ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [aberto]);

  if (!aberto) return null;

  // r112: botões reutilizáveis (mesmo visual, cores adaptadas pra topo vs rodapé)
  // r132: aceita botoesExtras (renderizados antes de Cancelar)
  const renderBotoes = (notopo) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* Botões extras opcionais — sempre antes dos padrão */}
      {botoesExtras}
      <button
        onClick={onFechar}
        style={{
          padding: '8px 18px',
          border: notopo ? '1px solid rgba(255,255,255,0.5)' : `1px solid ${COR.borda}`,
          borderRadius: '5px',
          backgroundColor: 'transparent',
          color: notopo ? COR.branco : COR.texto,
          fontSize: '13px', cursor: 'pointer',
          fontFamily: "'Manrope', sans-serif",
        }}
      >
        ✕ {somenteLeitura ? 'Fechar' : 'Cancelar'}
      </button>

      {somenteLeitura && onEditar && (
        <button
          onClick={onEditar}
          style={{
            padding: '8px 20px', border: 'none', borderRadius: '5px',
            backgroundColor: notopo ? COR.branco : COR.azul,
            color: notopo ? COR.azul : COR.branco,
            fontSize: '13px', fontWeight: '700', cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
          }}
        >Alterar</button>
      )}

      {!somenteLeitura && (
        <button
          onClick={onSalvar}
          disabled={salvando}
          style={{
            padding: '8px 22px', border: 'none', borderRadius: '5px',
            backgroundColor: notopo ? COR.branco : COR.azul,
            color: notopo ? COR.azul : COR.branco,
            fontSize: '13px', fontWeight: '700',
            cursor: salvando ? 'default' : 'pointer',
            opacity: salvando ? 0.7 : 1,
            fontFamily: "'Manrope', sans-serif",
          }}
        >{salvando ? 'Salvando...' : '✓ Salvar'}</button>
      )}
    </div>
  );

  return (
    <div
      onClick={onFechar}
      style={{
        // r232: FIXED, nao absolute. O <main> do PortalLayout tem
        // position:relative, entao o 'absolute' ancorava o overlay no topo da
        // PAGINA INTEIRA (que e tao alta quanto a lista). Com a lista rolada,
        // o modal abria la em cima, fora da area visivel — e como este mesmo
        // componente trava o scroll do body (useEffect acima), nao dava nem pra
        // rolar ate ele: a tela parecia travada. 'fixed' ancora na viewport, e
        // o modal abre sempre onde o usuario esta.
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        zIndex: 200, padding: '24px 16px',
        overflowY: 'auto',
        fontFamily: "'Manrope', sans-serif",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          width: '100%',
          maxWidth: `min(${largura}, 95vw)`,
          maxHeight: 'calc(100% - 48px)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          margin: '0 auto',
        }}
      >
        {/* Cabeçalho — r112: pode ter ícone SVG da rotina + descrição + botões no topo */}
        <div style={{
          backgroundColor: COR.azul,
          padding: '14px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '14px', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
            {/* Ícone SVG da rotina (opcional) */}
            {rotina && (
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                {iconeDaRotina(rotina, { width: 36, height: 36, color: COR.branco })}
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: '16px', fontWeight: '700', color: COR.branco,
                lineHeight: 1.2, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{titulo}</div>
              {descricao && (
                <div style={{
                  fontSize: '12px', opacity: 0.85, color: COR.branco,
                  marginTop: '3px',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{descricao}</div>
              )}
            </div>
          </div>

          {/* Botões no topo (opcional) */}
          {botoesNoTopo && renderBotoes(true)}

          {/* r113: removido botão × do header — fechar é responsabilidade do Cancelar */}
        </div>

        {/* Abas (opcionais) */}
        {abas && abas.length > 0 && (
          <div style={{ display: 'flex', borderBottom: `1px solid ${COR.borda}`, flexShrink: 0 }}>
            {abas.map(aba => (
              <button key={aba.key} onClick={() => onAba(aba.key)} style={{
                flex: 1, padding: '10px 16px', border: 'none',
                borderBottom: abaAtiva === aba.key ? `2px solid ${COR.azul}` : '2px solid transparent',
                backgroundColor: 'transparent',
                fontSize: '13px', fontWeight: abaAtiva === aba.key ? '600' : '400',
                color: abaAtiva === aba.key ? COR.azul : '#888',
                cursor: 'pointer', transition: 'color 0.15s',
                whiteSpace: 'nowrap',
                fontFamily: "'Manrope', sans-serif",
              }}>{aba.label}</button>
            ))}
          </div>
        )}

        {/* Corpo com scroll */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {children}
        </div>

        {/* Rodapé — r112: só aparece se botoesNoTopo === false (compatibilidade) */}
        {!botoesNoTopo && (
          <div style={{
            padding: '12px 20px',
            borderTop: `1px solid ${COR.borda}`,
            display: 'flex', gap: '8px', justifyContent: 'flex-end',
            flexShrink: 0,
            backgroundColor: '#fafafa',
          }}>
            {renderBotoes(false)}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
